import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { positionDropdown } from '../../../../common/dropdown'
import useDidUpdateEffect from '../../../../common/hooks/useDidUpdateEffect'
import { Element } from '../../../../common/html/element'
import { Tooltip } from '../../../../common/tooltip'
import { debounce } from '../../../../helpers/common'
import { InputCompany } from '../../../common/InputCompany'
import { InputSearchDropdown } from '../../../common/InputSearchDropdown'
import { FILTER_BY } from '../../../constant'
import {
  changeFilter,
  changeListPersonSearch,
  selectFilter,
  selectListPeronSearch,
  selectLoading,
} from '../../store/slice'
import { getSearchPerson } from '../../store/thunk'
import classes from './index.module.scss'

export const SelectPerson = ({
  companyRef,
  onPersonRef,
  companyDropdownRef,
}) => {
  const personRef = useRef()
  const dispatch = useDispatch()
  const searchCompanyAndPerson = useSelector(
    selectFilter('searchCompanyAndPerson'),
  )
  const filterPerson = useSelector(selectFilter('filterPerson'))
  const dataSearchPerson = useSelector(selectListPeronSearch)
  const isLoadingSearch = useSelector(selectLoading('searchPerson'))
  const locale = useSelector((state) => state.i18n.locale)

  const [inputValue, setInputValue] = useState('')

  const searchPersonIds = searchCompanyAndPerson.map((item) => item.personId)
  const dataSearchFormat = dataSearchPerson.filter(
    (item) => !searchPersonIds.includes(item.personId),
  )

  const handleChangeText = useCallback(
    debounce((value, organizationId) => {
      if (!value.trim()) {
        dispatch(changeListPersonSearch([]))
        return
      }
      dispatch(
        getSearchPerson({
          PersonSearch: value.trim(),
          OrganizationId: organizationId,
          Page: 1,
          PageSize: 100,
        }),
      )
    }, 700),
    [],
  )

  const handleOnFocus = () => {
    if (!dataSearchPerson.length) {
      handleChangeText(inputValue)
    }
  }

  const handleChoose = (value) => {
    setInputValue('')
    dispatch(
      changeFilter({
        searchCompanyAndPerson: [
          ...searchCompanyAndPerson,
          {
            type: FILTER_BY.PERSON,
            ...value,
          },
        ],
      }),
    )
  }

  const renderElement = () => {
    return (
      <InputCompany
        containerStyle={{
          flex: 1,
          width: '100%',
          height: '28px',
        }}
        placeHolder="connectMap.main.SEARCH_PERSON_NAME"
        type="text"
        isSearch
        onChange={setInputValue}
        value={inputValue}
        handleOnFocus={handleOnFocus}
      />
    )
  }

  const renderDropdown = ({ hideDropdown }) => {
    return (
      <div className={classes.dropdownContainer}>
        {/* Table Headers */}
        <div className={classes.tableHeader}>
          <Element className={classes.personNameColumn}>
            <Translate value="connectMap.main.INDIVIDUAL" />
          </Element>
          <Element className={classes.shareholdingColumn}>
            <Translate value="connectMap.main.COMPANIES_WITH_OWNERSHIP" />
          </Element>
          <Element className={classes.managementColumn}>
            <Translate value="connectMap.main.COMPANIES_WITH_EXECUTIVE_ROLES" />
          </Element>
        </div>
        <div className={classes.dropdownContent}>
          {dataSearchFormat?.map((item) => (
            <DropdownItem
              key={item?.personId}
              item={item}
              hideDropdown={hideDropdown}
              handleChoose={handleChoose}
            />
          ))}
        </div>
      </div>
    )
  }

  const renderNodata = () => {
    if (!inputValue) return null
    return (
      <Element style={{ fontStyle: 'italic', padding: 12 }}>
        <Translate value="connectMap.main.NODATA_ADD_COMPANY" />
      </Element>
    )
  }

  useDidUpdateEffect(() => {
    if (inputValue.length > 2) {
      handleChangeText(inputValue, filterPerson?.organizationId)
    } else {
      dispatch(changeListPersonSearch([]))
    }
  }, [inputValue, locale, filterPerson])

  useEffect(() => {
    if (onPersonRef) {
      onPersonRef(personRef)
    }
  }, [onPersonRef])

  return (
    <InputSearchDropdown
      style={{ width: '100%' }}
      renderElement={renderElement}
      renderDropdown={renderDropdown}
      dropdownItemHeight={50}
      maxDropdownItem={10}
      data={dataSearchFormat}
      dropdownPosition={positionDropdown.BOTTOM_CENTER}
      isLoading={isLoadingSearch}
      renderNodata={renderNodata}
      isValidateTwoKey
      textInput={inputValue}
      exceptRef={[personRef, companyRef, companyDropdownRef]}
    />
  )
}

const DropdownItem = ({
  item,
  hideDropdown,
  handleChoose,
  noHiddenWhenClick,
}) => {
  const [bgColor, setBgColor] = useState('transparent')

  const onMouseEnter = () => {
    setBgColor('#F3F3F3')
  }

  const onMouseLeave = () => {
    setBgColor('transparent')
  }

  const formatShareholdings = () => {
    if (!item?.shareholdings || item.shareholdings.length === 0) {
      return ''
    }
    return item.shareholdings.map((share) => share.organizationName).join(', ')
  }

  const formatManagementRoles = () => {
    if (!item?.managementRoles || item.managementRoles.length === 0) {
      return ''
    }
    return item.managementRoles.map((role) => role.organizationName).join(', ')
  }

  return (
    <Element
      onClick={() => {
        handleChoose(item)
        !noHiddenWhenClick && hideDropdown(false)
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ backgroundColor: bgColor }}
      className={classes.dropdownItem}
    >
      <div className={classes.tableRow}>
        <div className={classes.personNameColumn}>
          <Element
            elementName="p"
            style={{
              color: '#191d25',
              textTransform: 'uppercase',
              margin: 0,
              fontWeight: 'bold',
            }}
          >
            {item?.fullName}
          </Element>
        </div>
        <div className={classes.shareholdingColumn}>
          {item?.shareholdings && item.shareholdings.length > 0 ? (
            <Tooltip
              tooltipContent={formatShareholdings()}
              position={positionDropdown.RIGHT_CENTER}
              zIndex={15}
            >
              <Element className={classes.commaSeparatedList}>
                {formatShareholdings()}
              </Element>
            </Tooltip>
          ) : (
            <span style={{ color: '#999' }}>-</span>
          )}
        </div>
        <div className={classes.managementColumn}>
          {item?.managementRoles && item.managementRoles.length > 0 ? (
            <Tooltip
              tooltipContent={formatManagementRoles()}
              position={positionDropdown.RIGHT_CENTER}
              zIndex={15}
            >
              <Element className={classes.commaSeparatedList}>
                {formatManagementRoles()}
              </Element>
            </Tooltip>
          ) : (
            <span style={{ color: '#999' }}>-</span>
          )}
        </div>
      </div>
    </Element>
  )
}
